/**
 * A consulta da oportunidade (#811, clínica): médico, especialidade, data e
 * hora, e onde a consulta está no sistema de agenda da clínica.
 *
 * Só nas organizações com a funcionalidade "clinica" ligada; sem ela, as rotas
 * respondem 404. O atendente escolhe médico, especialidade e horário. O
 * `agendamento` (os códigos do paciente e da marcação na agenda da clínica)
 * só a integração grava, e a tela o mostra só para leitura.
 */

import { z } from 'zod';

/**
 * Um médico ou uma especialidade. O `codigo` é o do SISAC (`codigo_sisac`), que
 * a agenda da clínica usa como chave: a tela o manda nas operações de vaga e de
 * agendamento, onde `id` (o do CliqCRM) não serve. Não é dado de pessoa.
 */
const OpcaoSchema = z.object({ id: z.string().uuid(), nome: z.string(), codigo: z.string() });

export const ConsultaDaOportunidadeSchema = z.object({
  medico: OpcaoSchema.nullable(),
  /** Dado de saúde: revela a condição do paciente. */
  especialidade: OpcaoSchema.nullable(),
  /** Instante ISO 8601. */
  consulta_em: z.string().datetime().nullable(),
  /** Gravado só pela integração com a agenda da clínica, ao agendar. */
  agendamento: z
    .object({
      codigo_do_paciente: z.string().nullable(),
      codigo_da_agenda: z.string().nullable(),
    })
    .nullable(),
  versao: z
    .number()
    .int()
    .min(1)
    .nullable()
    .describe('A versão lida. Devolva em `versao` ao salvar; nula enquanto não há consulta.'),
  /**
   * O código do paciente no SISAC a que o cliente da oportunidade está
   * vinculado (#1024): com ele, a tela não pede o CPF de novo. Nulo sem vínculo.
   */
  paciente_vinculado: z.string().nullable().default(null),
  /**
   * O cliente da oportunidade, para a tela procurar e vincular o paciente pelas
   * rotas do cliente (`/v1/customers/{id}/paciente`). Nulo sem cliente.
   */
  cliente_id: z.string().uuid().nullable().default(null),
  /**
   * Como a escrita no sistema da clínica foi conferida (#1187), só na resposta
   * de agendar, remarcar e cancelar: `conferida` (a leitura bateu),
   * `nao_conferida` (a escrita passou, a leitura não respondeu) ou
   * `sem_conferencia` (a organização não ligou uma). Ausente nas leituras.
   */
  conferencia: z.enum(['conferida', 'nao_conferida', 'sem_conferencia']).optional(),
});
export type ConsultaDaOportunidade = z.infer<typeof ConsultaDaOportunidadeSchema>;

/** O que o atendente escolhe. `null` tira a escolha. */
export const SalvarConsultaDaOportunidadeSchema = z
  .object({
    medico_id: z.string().uuid().nullable(),
    especialidade_id: z.string().uuid().nullable(),
    consulta_em: z.string().datetime({ offset: true }).nullable(),
    versao: z.number().int().min(1).nullable(),
  })
  .strict();
export type SalvarConsultaDaOportunidade = z.infer<typeof SalvarConsultaDaOportunidadeSchema>;

/** As opções ativas, mantidas pela agenda da clínica. */
export const OpcoesDaConsultaSchema = z.object({
  medicos: z.array(OpcaoSchema),
  especialidades: z.array(OpcaoSchema),
});
export type OpcoesDaConsulta = z.infer<typeof OpcoesDaConsultaSchema>;

/**
 * Puxa a lista de um tipo de opção (médico ou especialidade) de uma operação de
 * leitura do serviço de automações e a aplica (cria, renomeia, inativa o que não
 * veio). `operacao_id` é a operação publicada que devolve `{ codigo, nome }`.
 */
export const SincronizarOpcoesDaConsultaSchema = z
  .object({
    tipo: z.enum(['medico', 'especialidade']),
    operacao_id: z.string().uuid(),
  })
  .strict();
export type SincronizarOpcoesDaConsulta = z.infer<typeof SincronizarOpcoesDaConsultaSchema>;

/** O que a sincronização fez — sem nomes, só contagens. */
export const ResultadoDaSincronizacaoDeOpcoesSchema = z.object({
  criados: z.number().int().min(0),
  atualizados: z.number().int().min(0),
  inativados: z.number().int().min(0),
});
export type ResultadoDaSincronizacaoDeOpcoes = z.infer<
  typeof ResultadoDaSincronizacaoDeOpcoesSchema
>;

/**
 * Agenda a consulta no SISAC: roda a operação de ESCRITA que preenche a vaga
 * (`operacao_id`, com `parametros` por nome) e, se preencheu, grava os códigos do
 * paciente e da agenda na consulta. Vaga já tomada → 409, nada é gravado.
 *
 * Os parâmetros são os campos do agendamento como a recepção os preenche (os
 * nomes em `PARAMETROS_DA_AGENDA.agendar`). Sem cadastro no SISAC, o paciente
 * vai só pelo nome e pelos dados digitados, e `codigo_do_paciente` vai nulo:
 * a agenda da clínica aceita a vaga assim.
 */
export const AgendarConsultaSchema = z
  .object({
    operacao_id: z.string().uuid(),
    parametros: z.record(z.string(), z.string()),
    agendamento: z.object({
      codigo_do_paciente: z.string().min(1).nullable(),
      codigo_da_agenda: z.string().min(1),
      consulta_em: z.string().datetime({ offset: true }).nullable().optional(),
    }),
  })
  .strict();
export type AgendarConsulta = z.infer<typeof AgendarConsultaSchema>;

/**
 * Remarcar a consulta marcada (#1187): o agendar de novo, com os dados novos e a
 * vaga nova (ou a mesma). A vaga de antes e o paciente saem da consulta que o
 * CRM gravou, nunca do corpo. A `versao` é a da consulta que a tela leu.
 */
export const RemarcarConsultaSchema = z
  .object({
    operacao_id: z.string().uuid(),
    parametros: z.record(z.string(), z.string()),
    agendamento: z.object({
      codigo_da_agenda: z.string().min(1),
      consulta_em: z.string().datetime({ offset: true }).nullable().optional(),
    }),
    versao: z.number().int().min(1),
  })
  .strict();
export type RemarcarConsulta = z.infer<typeof RemarcarConsultaSchema>;

/**
 * Cancela o agendamento da consulta no SISAC (Fase 3): a vaga é a que o CRM
 * gravou ao agendar, e o paciente, o dele. A `versao` é a da consulta que a tela
 * leu: outra pessoa reagendou no meio, e o cancelamento não leva a vaga nova.
 */
export const CancelarConsultaSchema = z
  .object({
    operacao_id: z.string().uuid(),
    /**
     * O código do motivo, da lista `motivos_de_cancelamento` da clínica. Só
     * código: texto livre aqui seria dado sobre o paciente fora da observação.
     */
    motivo: z
      .string()
      .trim()
      .regex(/^[A-Za-z0-9_.-]{1,20}$/, 'O motivo é o código da lista da clínica.'),
    /** O paciente fica na fila de espera do SISAC, para uma vaga que abrir. */
    fila_de_espera: z.boolean(),
    observacao: z.string().trim().max(500).optional(),
    versao: z.number().int().min(1),
  })
  .strict();
export type CancelarConsulta = z.infer<typeof CancelarConsultaSchema>;

/**
 * Cadastra o paciente da oportunidade na agenda da clínica (#1015): roda a
 * operação de escrita `cadastrar_paciente` do mapa da organização, com os dados
 * por nome.
 */
export const CadastrarPacienteNaAgendaSchema = z
  .object({
    operacao_id: z.string().uuid(),
    parametros: z.record(z.string(), z.string()),
  })
  .strict();
export type CadastrarPacienteNaAgenda = z.infer<typeof CadastrarPacienteNaAgendaSchema>;

/** O que volta do cadastro: só o código do paciente no SISAC, nunca as linhas dele. */
export const PacienteCadastradoNaAgendaSchema = z.object({
  /** Nulo quando a operação não devolve o código. */
  codigo_do_paciente: z.string().nullable(),
});
export type PacienteCadastradoNaAgenda = z.infer<typeof PacienteCadastradoNaAgendaSchema>;

/**
 * O paciente vinculado ao cliente da oportunidade (#1024), como a aba Consulta o
 * mostra: o código sai do vínculo, no servidor; do CPF, só os dois últimos
 * dígitos. Sem vínculo, `paciente` nulo.
 */
/**
 * O que o formulário do agendamento pré-preenche do cadastro do vinculado. Só os
 * campos que o agendamento leva; datas em `AAAA-MM-DD`.
 */
const DadosDoPacienteParaAgendarSchema = z.object({
  nascimento: z.string().nullable(),
  sexo: z.string().nullable(),
  cep: z.string().nullable(),
  cod_convenio: z.string().nullable(),
  plano: z.string().nullable(),
  matricula: z.string().nullable(),
  celular: z.string().nullable(),
  whatsapp: z.string().nullable(),
  email: z.string().nullable(),
  /**
   * O endereço e o documento do cadastro (#1187), para o agendamento já chegar
   * com eles. Nulos quando a operação `ler_paciente` não os lê.
   */
  endereco: z.string().nullable().default(null),
  numero: z.string().nullable().default(null),
  bairro: z.string().nullable().default(null),
  cidade: z.string().nullable().default(null),
  uf: z.string().nullable().default(null),
  rg: z.string().nullable().default(null),
  orgao_emissor: z.string().nullable().default(null),
});
export type DadosDoPacienteParaAgendar = z.infer<typeof DadosDoPacienteParaAgendarSchema>;

export const PacienteVinculadoDaConsultaSchema = z.object({
  paciente: z
    .object({
      codigo: z.string(),
      nome: z.string().nullable(),
      cpf_final: z.string().nullable(),
      /** Para pré-preencher o agendamento; nulo quando só o código veio. */
      dados: DadosDoPacienteParaAgendarSchema.nullable().default(null),
    })
    .nullable(),
  /** `fora`: o SISAC não respondeu, e só o código veio. */
  sisac: z.enum(['ok', 'fora']),
});
export type PacienteVinculadoDaConsulta = z.infer<typeof PacienteVinculadoDaConsultaSchema>;

/**
 * Roda uma operação de LEITURA da agenda da clínica (buscar paciente por CPF,
 * vagas livres, etc.). Só leitura — escrita é recusada aqui. A resposta é genérica
 * (as linhas que a operação devolve), então o contrato não acopla ao schema do SISAC.
 */
export const ConsultaLeituraSchema = z
  .object({
    /**
     * A oportunidade de onde a tela lê: quem lê precisa vê-la, na carteira dele,
     * e a busca de paciente entra no registro de acesso do cliente dela.
     */
    oportunidade_id: z.string().uuid(),
    operacao_id: z.string().uuid(),
    parametros: z.record(z.string(), z.string()),
  })
  .strict();
export type ConsultaLeitura = z.infer<typeof ConsultaLeituraSchema>;

/**
 * As partes da agenda da clínica (ADR-0028), com os parâmetros que a tela manda
 * a cada uma, por nome. A operação declara os que usa, pelos nomes daqui (os
 * que ela não declara não vão), ou todos, na ordem daqui, com outros nomes.
 */
/** Os campos do agendar, que o remarcar também leva (#1187). */
const CAMPOS_DO_AGENDAR = [
  'cod_paciente',
  'id_agenda',
  'cod_convenio',
  'nome',
  'data_nasc',
  'sexo',
  'cpf',
  'cep',
  'plano',
  'matricula',
  'tipo',
  'cod_especialidade',
  'cod_medico',
  'local',
  'origem',
  'celular',
  'whatsapp',
  'email',
  'telemedicina',
  'urgente',
  'observacao',
  // O endereço e o documento do paciente na agenda (#1187); `uf` vai na coluna `Estado`.
  'endereco',
  'numero',
  'bairro',
  'cidade',
  'uf',
  'rg',
  'orgao_emissor',
] as const;

export const PARAMETROS_DA_AGENDA = {
  buscar_paciente: ['cpf'],
  buscar_paciente_por_telefone: ['telefone'],
  buscar_paciente_por_termo: ['termo'],
  ler_paciente: ['cod_paciente'],
  historico_paciente: ['cod_paciente'],
  ultimas_vezes_paciente: ['cod_paciente'],
  /**
   * Os exames que o paciente fez (#1144), para o prontuário: `cod_paciente`,
   * `data_hora` e `medico`, e o `id_exame` se houver. Só pelo código do vínculo;
   * a linha de outro `cod_paciente` é descartada. O nome do exame não aparece
   * no resumo (ADR-0022). O `historico_paciente` também devolve `cod_paciente`.
   */
  exames_paciente: ['cod_paciente'],
  /**
   * O que cada card do funil é no SISAC (#1151, ADR-0031): as referências que
   * as rotinas ligaram aos cards, separadas por vírgula. Uma linha por
   * referência, com `referencia`, `tipo`, `servico`, `medico`, `agendado_em`,
   * `marcado_para` e `valor`. A linha de referência que não foi pedida é
   * descartada.
   */
  detalhes_dos_atendimentos: ['referencias'],
  vagas: ['cod_medico', 'inicio', 'fim'],
  /**
   * Os dias com vaga livre do médico num período (#1187): `dia` (`AAAA-MM-DD`)
   * e `vagas` (quantas). O calendário pede um mês de cada vez, e só depois as
   * vagas do dia escolhido: um mês inteiro de vagas de minuto em minuto
   * passaria do teto de linhas da operação.
   */
  dias_com_vaga: ['cod_medico', 'inicio', 'fim'],
  bloqueios: ['cod_medico', 'inicio', 'fim'],
  medicos: [],
  medicos_por_especialidade: ['cod_especialidade'],
  especialidades: [],
  convenios: [],
  planos: ['cod_convenio'],
  origens: [],
  locais: [],
  cadastrar_paciente: ['nome', 'cpf', 'sexo', 'data_nasc', 'celular', 'email'],
  agendar: CAMPOS_DO_AGENDAR,
  /**
   * Remarcar (#1187): o "Editar" da consulta marcada. Os campos do agendar e a
   * vaga de antes; a operação passa o paciente para a vaga nova e devolve a
   * antiga, numa escrita só. A mesma vaga só atualiza os dados.
   */
  remarcar: [...CAMPOS_DO_AGENDAR, 'id_agenda_anterior'],
  /**
   * O agendamento gravado na vaga (#1187), para o "Editar" começar com ele:
   * devolve as colunas com os nomes do agendar.
   */
  ler_agendamento: ['id_agenda'],
  /**
   * Cancelar o agendamento (Fase 3): devolve a vaga, com o motivo, a fila de
   * espera (`1` ou `0`) e a observação. O código do paciente vai junto para a
   * operação conferir que a vaga ainda é dele.
   */
  liberar_vaga: ['id_agenda', 'cod_paciente', 'id_motivo', 'fila_de_espera', 'observacao'],
  /**
   * Registrar o cancelamento (#1187): o que a "Exclusão de horário" do SISAC
   * grava, com o motivo. Roda antes de liberar a vaga; sem o registro, nada é
   * cancelado. Os mesmos campos do cancelar.
   */
  registrar_cancelamento: [
    'id_agenda',
    'cod_paciente',
    'id_motivo',
    'fila_de_espera',
    'observacao',
  ],
  /** Os motivos de cancelamento da clínica: um cadastro, sem dado de paciente. */
  motivos_de_cancelamento: [],
  /**
   * Os preços particulares da clínica (#1084), sem dado de paciente. A das
   * consultas devolve `cod_medico` (vazio no padrão da clínica),
   * `cod_procedimento`, `nome` e `preco` (em reais); a dos procedimentos,
   * `cod_procedimento`, `nome`, `grupo`, `tipo` (`exame` ou `procedimento`) e
   * `preco`.
   */
  precos_de_consulta: [],
  precos_de_procedimentos: [],
} as const satisfies Readonly<Record<string, readonly string[]>>;

export type FinalidadeDaAgenda = keyof typeof PARAMETROS_DA_AGENDA;
export const FINALIDADES_DA_AGENDA = Object.keys(PARAMETROS_DA_AGENDA) as FinalidadeDaAgenda[];

/**
 * O que o "Editar" pré-preenche (#1187): as colunas que a `ler_agendamento`
 * devolve, com os nomes do agendar. O nome e o CPF ficam de fora: com o
 * paciente vinculado, saem do cadastro dele.
 */
export const CAMPOS_DO_AGENDAMENTO_LIDO = [
  // O nome gravado na vaga: a confirmação do cancelamento o mostra quando não há vínculo (#1187).
  'nome',
  'data_nasc',
  'sexo',
  'cep',
  'cod_convenio',
  'plano',
  'matricula',
  'tipo',
  'local',
  'origem',
  'celular',
  'whatsapp',
  'email',
  'telemedicina',
  'urgente',
  'observacao',
  'endereco',
  'numero',
  'bairro',
  'cidade',
  'uf',
  'rg',
  'orgao_emissor',
] as const;
export const AgendamentoDaConsultaSchema = z.object({
  /** Nulo sem a parte ligada, ou com o sistema da clínica fora (`sisac`). */
  dados: z
    .object(
      Object.fromEntries(
        CAMPOS_DO_AGENDAMENTO_LIDO.map((campo) => [campo, z.string().nullable()]),
      ) as Record<(typeof CAMPOS_DO_AGENDAMENTO_LIDO)[number], z.ZodNullable<z.ZodString>>,
    )
    .nullable(),
  sisac: z.enum(['ok', 'fora', 'desligada']),
});
export type AgendamentoDaConsulta = z.infer<typeof AgendamentoDaConsultaSchema>;

/** As partes que gravam no sistema da clínica; as outras só leem. */
export const ESCRITAS_DA_AGENDA: readonly FinalidadeDaAgenda[] = [
  'cadastrar_paciente',
  'agendar',
  'remarcar',
  'liberar_vaga',
  'registrar_cancelamento',
];

/**
 * Os padrões do agendamento da organização (#1187): com o que o formulário
 * começa. São códigos do sistema da clínica, os mesmos que as listas da agenda
 * devolvem (o local e o convênio pelo código; o plano e a origem pelo nome, que
 * é o código deles). Ausente é sem padrão: a pessoa escolhe.
 */
/** Os campos do agendamento que têm padrão (e que podem ficar escondidos). */
/*
 * O tipo não tem padrão: o formulário já começa em "Consulta", e retorno ou
 * exame é escolha de cada agendamento.
 */
export const CAMPOS_COM_PADRAO = ['local', 'origem', 'convenio', 'plano'] as const;
export type CampoComPadrao = (typeof CAMPOS_COM_PADRAO)[number];

export const PadroesDaAgendaSchema = z
  .object({
    local: z.string().trim().min(1).max(30).optional(),
    origem: z.string().trim().min(1).max(30).optional(),
    convenio: z.string().trim().min(1).max(30).optional(),
    plano: z.string().trim().min(1).max(30).optional(),
    /**
     * Os campos que não aparecem no formulário (#1187): é sempre o padrão, e a
     * recepção nem o vê. Só vale para campo com padrão; sem valor, ele aparece.
     */
    ocultos: z.array(z.enum(CAMPOS_COM_PADRAO)).max(CAMPOS_COM_PADRAO.length).optional(),
  })
  .strict();
export type PadroesDaAgenda = z.infer<typeof PadroesDaAgendaSchema>;

/**
 * As listas que a tela "Agenda da clínica" lê para escolher os padrões (#1187):
 * cadastros da clínica, sem dado de paciente. Os planos são os de um convênio.
 */
export const LISTAS_DOS_PADROES = ['locais', 'origens', 'convenios', 'planos'] as const;
export const PedidoDeListaDaAgendaSchema = z
  .object({
    lista: z.enum(LISTAS_DOS_PADROES),
    /** Só nos planos: o convênio deles. */
    cod_convenio: z.string().trim().min(1).max(30).optional(),
  })
  .strict();
export type PedidoDeListaDaAgenda = z.infer<typeof PedidoDeListaDaAgendaSchema>;
export const ListaDaAgendaSchema = z.object({
  itens: z.array(z.object({ codigo: z.string(), nome: z.string() })),
});
export type ListaDaAgenda = z.infer<typeof ListaDaAgendaSchema>;

/**
 * O mapa da agenda da organização: cada parte → a operação publicada no serviço
 * de automações. Parte ausente é parte não ligada.
 */
/**
 * A escrita conferida (#1187): depois de gravar, o CRM roda uma leitura que a
 * clínica escolheu, com a mesma vaga (`id_agenda`), e compara os campos
 * escolhidos. No agendar e no remarcar, eles têm de voltar como foram; no
 * cancelar (`liberar_vaga`), têm de voltar vazios. Divergiu: 409, com os nomes
 * dos campos, e nada muda no CRM.
 */
export const ESCRITAS_CONFERIDAS = ['agendar', 'remarcar', 'liberar_vaga'] as const;
export type EscritaConferida = (typeof ESCRITAS_CONFERIDAS)[number];
export const ConferenciaDaEscritaSchema = z
  .object({
    /** A leitura que confere: publicada, da mesma conexão, recebe `id_agenda`. */
    operacao: z.string().uuid(),
    /** Os campos comparados, pelos nomes do agendar; pelo menos um. */
    campos: z.array(z.string().min(1).max(40)).min(1).max(40),
  })
  .strict();
export type ConferenciaDaEscrita = z.infer<typeof ConferenciaDaEscritaSchema>;
export const ConferenciasDaAgendaSchema = z
  .object({
    agendar: ConferenciaDaEscritaSchema.optional(),
    remarcar: ConferenciaDaEscritaSchema.optional(),
    liberar_vaga: ConferenciaDaEscritaSchema.optional(),
  })
  .strict();
export type ConferenciasDaAgenda = z.infer<typeof ConferenciasDaAgendaSchema>;

export const AgendaDaClinicaSchema = z
  .object({
    operacoes: z
      .record(z.string(), z.string().uuid())
      .refine(
        (mapa) => Object.keys(mapa).every((parte) => parte in PARAMETROS_DA_AGENDA),
        'Parte da agenda desconhecida.',
      ),
    /**
     * A versão do mapa: o GET e o PUT a devolvem. No PUT, a versão que a tela
     * leu; se outra pessoa salvou no meio, 409 `CONFLITO_DE_VERSAO` e nada é
     * gravado. Opcional no corpo, para quem ainda não a manda.
     */
    versao: z.string().max(64).optional(),
    /** Os padrões do formulário do agendamento (#1187). Ausente no PUT: não mexe. */
    padroes: PadroesDaAgendaSchema.optional(),
    /** As conferências das escritas (#1187). Ausente no PUT: não mexe. */
    conferencias: ConferenciasDaAgendaSchema.optional(),
  })
  .strict();
export type AgendaDaClinica = z.infer<typeof AgendaDaClinicaSchema>;
